// Domain (view) model for feature chat — what the panel renders, with none of the wire's shape.
import type { MessageCredits } from './orpheus.types'
import type { OrpheusKeyError } from './orpheusKey.types'
import type { FilePreview, SetupCard, SetupStep } from './orpheusSetup.types'

/**
 * Backend `feature_key`s the v4 app currently opens the panel for. The catalog
 * (`GET /feature-chat/features`) remains the source of truth for what exists; this is only the
 * set v4 has an entry point for, so a host cannot invent a key the registry would 400 on.
 */
export const FEATURE_CHAT_KEYS = {
  ALERT: 'alert',
  ANOMALIES: 'anomalies',
  CAUSAL_SINGLE: 'causal_single',
  CREATIVE_AI: 'creative_ai',
  INSIGHTS: 'insights',
  /** Setup mode (KUR-9, ORP-22826): target-less; the user describes the alert to set up. */
  ALERT_CREATE: 'alert_create',
} as const

export type FeatureChatKey = (typeof FEATURE_CHAT_KEYS)[keyof typeof FEATURE_CHAT_KEYS]

/** Always an object on the wire: the backend's `DictField` 400s on a scalar or a list. */
export type FeatureChatTargetRef = Record<string, unknown>

export type FeatureChatRole = 'user' | 'assistant'

/**
 * Why a turn failed, as a machine value.
 *
 * `rateLimited` and `creditDepleted` are separate surfaces with separate remedies — one is "wait",
 * the other is "buy or upgrade" — and the backend keeps them apart (429 + `Retry-After` vs. 402
 * `CREDIT_DEPLETED`). Collapsing them into one "something went wrong" is the failure this type
 * exists to prevent.
 */
export type FeatureChatErrorKind = 'creditDepleted' | 'generic' | 'rateLimited'

export interface FeatureChatTurnError {
  kind: FeatureChatErrorKind
  /** Whole seconds from `Retry-After`, rounded up. Null unless the server named a wait. */
  retryAfterSeconds: number | null
  /** The backend's user-visible text when it sent one; null means the panel renders its own copy. */
  message: string | null
}

export interface FeatureChatMessage {
  /** Stable list key. Survives the optimistic-to-answered transition. */
  localId: string
  role: FeatureChatRole
  content: string
  createdAt: string | null
  /** True while the assistant turn is in flight (the typing placeholder). */
  pending: boolean
  citations: string[]
  followups: string[]
  /** The read the answer is grounded on came back short; the panel hints to narrow the ref. */
  needsMoreContext: boolean
  /** Set on the assistant turn that failed; `content` is empty in that case. */
  error: FeatureChatTurnError | null
  /** A setup-mode turn's parts (ORP-26203) — the same cards the main chat draws. */
  setupStep?: SetupStep | null
  setupCard?: SetupCard | null
  filePreview?: FilePreview | null
  credits?: MessageCredits | null
  /** The account's own key failed; the card replaces the answer (never replayable here). */
  keyError?: OrpheusKeyError
}

export type FeatureChatMode = 'talk' | 'setup'

/**
 * The panel's opening width in px (v3 `FeatureOrpheusPanel`'s `DEFAULT_PANEL_WIDTH`). Exported
 * because every host offsets its own drawer by it to sit beside the panel, and only learns the real
 * width once the user drags — a host-local literal would silently disagree with the panel here.
 */
export const FEATURE_CHAT_PANEL_WIDTH = 500
